import { consoleInfoLog, isLocal } from 'App/Helpers';
import { captureCustomException } from 'App/Services/ErrorTracking/captureCustomException';
import OneSignal from 'react-onesignal';

import { ONESIGNAL_INIT_ERROR } from './constants';

/**
 * Boots the OneSignal SDK and wires product handlers onto the shared listeners.
 *
 * @param {Object} params
 * @param {string} params.oneSignalAppId
 * @param {(ready: boolean) => void} params.setPushServiceReady
 * @param {boolean} params.requiresUserPrivacyConsent
 * @param {() => void} [params.onPermissionPromptDisplay]
 * @param {(permission: boolean) => void} [params.onPermissionChange]
 * @param {(event: Object) => void} [params.onPushSubscriptionChange]
 * @returns {Promise<boolean>} Whether the SDK finished init.
 */
export const initOneSignal = ({
    oneSignalAppId,
    setPushServiceReady,
    onPermissionChange,
    onPermissionPromptDisplay,
    onPushSubscriptionChange,
    requiresUserPrivacyConsent,
}) => {
    if (typeof OneSignal.init !== 'function') {
        const error = new Error('OneSignal.init not available');

        captureCustomException(error, ONESIGNAL_INIT_ERROR);
        consoleInfoLog('## Init Failed: OneSignal.init missing', 'fail');
        return Promise.resolve(false);
    }

    return OneSignal.init({
        appId: oneSignalAppId,
        serviceWorkerPath: isLocal()
            ? 'Public/onesignal/OneSignalSDKWorker.js'
            : 'push/OneSignalSDKWorker.js',
        serviceWorkerParam: {
            scope: isLocal() ? '/Public/onesignal/' : '/push/',
        },
        requiresUserPrivacyConsent,
        allowLocalhostAsSecureOrigin: isLocal(),
        welcomeNotification: { disable: true },
        notifyButton: { enable: false },
        autoRegister: false,
    })
        .then(() => {
            OneSignal.Notifications.addEventListener(
                'permissionPromptDisplay',
                () => {
                    onPermissionPromptDisplay?.();
                }
            );
            OneSignal.User.PushSubscription.addEventListener(
                'change',
                (event) => {
                    onPushSubscriptionChange?.(event);
                }
            );
            OneSignal.Notifications.addEventListener(
                'permissionChange',
                (permission) => {
                    onPermissionChange?.(permission);
                }
            );

            setPushServiceReady(true);
            consoleInfoLog(
                `## Init: OneSignal with App Id: ${oneSignalAppId}`,
                'init'
            );

            return true;
        })
        .catch((error) => {
            captureCustomException(error, ONESIGNAL_INIT_ERROR);
            consoleInfoLog('## Init Failed: OneSignal', 'fail');
            return false;
        });
};
